import type { RecurrenceRule, Task } from '../db/models'
import { withoutSucceededRounds } from '../db/taskFilters'
import { descendantsOf } from '../db/taskTree'

/**
 * **Eine Aufgabe wird wiederkehrend, ihre Unteraufgaben wiederholten sich selbst** (#205, Punkt 5).
 * Beides zugleich gibt es nicht: Unter einer wiederkehrenden Aufgabe gilt ihr Rhythmus, ihre neue
 * Runde bringt die Unteraufgaben frisch mit — auf jeder Ebene (#181). Die Datenquelle nimmt den
 * Unteraufgaben deshalb ihre Wiederholung ab und meldet das hier — die Oberfläche sagt es dem
 * Nutzer und bietet `⌘Z` an.
 *
 * Ein Modul-Ereignis wie der Geschafft-Moment: Jeder Schreibweg geht durch die Datenquelle, und
 * die kennt keine Toasts.
 */
export interface ChildRecurrenceDropped {
  parentId: string
  /** Die Wiederholung der Aufgabe vor dem Schreiben — `⌘Z` stellt sie wieder her. */
  parentBefore: RecurrenceRule | null
  children: { id: string; recurrence: RecurrenceRule }[]
}

type Listener = (event: ChildRecurrenceDropped) => void
const listeners = new Set<Listener>()

export function onChildRecurrenceDropped(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function reportChildRecurrenceDropped(event: ChildRecurrenceDropped): void {
  if (event.children.length === 0) return
  for (const listener of [...listeners]) listener(event)
}

/**
 * Was seine Wiederholung verliert, wenn eine Zeile `changes` bekommt: Erhält sie eine Wiederholung
 * — Aufgabe oder Unteraufgabe auf jeder Ebene (#181, Grill 11) —, dann alles darunter, was in der
 * Checkliste steht. Ausgeblendete erledigte Runden sind samt Ast Geschichte: Mit ihrem Rhythmus
 * blieben sie in Geschafft, ohne ihn stünden sie wieder in der Checkliste und kämen mit jeder
 * neuen Runde mit.
 *
 * @param rows die Nachkommen von `parent` (gern mehr; gelöschte zählen nicht).
 */
export function descendantsLosingRecurrence(
  parent: Pick<Task, 'id'> | undefined,
  changes: Partial<Pick<Task, 'recurrence'>>,
  rows: readonly Task[],
): Task[] {
  if (!parent || changes.recurrence == null) return []
  const family = descendantsOf(rows, parent.id)
  return descendantsOf(rows, parent.id, siblings => withoutSucceededRounds(siblings, family)).filter(row => row.recurrence != null)
}
